import React, { useMemo } from 'react';

const RatingDistribution = ({ data }) => {
  const ratings = [5, 4, 3, 2, 1];
  
  // Count ratings
  const ratingCounts = useMemo(() => {
    return ratings.map(rating => {
      const count = data.filter(review => review.rating === rating).length;
      const percentage = data.length > 0 ? Math.round((count / data.length) * 100) : 0;
      return { rating, count, percentage };
    });
  }, [data, ratings]);
  
  // Calculate max count for proportional bars
  const maxCount = Math.max(...ratingCounts.map(r => r.count || 1));

  // Calculate average rating
  const averageRating = useMemo(() => {
    if (data.length === 0) return 0;
    const total = data.reduce((sum, review) => sum + review.rating, 0);
    return (total / data.length).toFixed(1);
  }, [data]);

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-lg font-medium mb-4">Rating Distribution</h2>
      
      <div className="space-y-3">
        {ratingCounts.map(({ rating, count, percentage }) => (
          <div key={rating} className="flex items-center">
            <div className="w-10 text-sm font-medium text-gray-700">{rating} ★</div>
            <div className="w-full ml-2">
              <div className="flex items-center">
                <div className="bg-gray-200 h-4 rounded-sm flex-grow">
                  <div 
                    className={`h-4 rounded-sm ${
                      rating >= 4 ? 'bg-green-500' : 
                      rating === 3 ? 'bg-yellow-400' : 
                      'bg-red-500'
                    }`}
                    style={{ width: maxCount > 0 ? `${(count / maxCount) * 100}%` : '0%' }}
                  ></div>
                </div>
                <div className="ml-3 min-w-[80px] text-sm text-gray-500">
                  {count} ({percentage}%)
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Average Rating */}
      <div className="mt-6 flex justify-between items-center p-4 bg-gray-50 rounded-lg">
        <div className="text-sm font-medium text-gray-700">Average Rating</div>
        <div className="flex items-center">
          <div className="text-2xl font-bold mr-2">
            {averageRating}
          </div>
          <div className="flex text-yellow-400">
            {/* Full stars */}
            {Array.from({ length: Math.floor(averageRating) }).map((_, i) => (
              <svg key={`full-${i}`} className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ))}
            
            {/* Partial star */}
            {averageRating % 1 !== 0 && (
              <div className="relative w-5 h-5 text-yellow-400">
                {/* Background star (gray) */}
                <svg className="absolute w-5 h-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                
                {/* Foreground partial star (yellow) */}
                <div className="absolute overflow-hidden" style={{ width: `${(averageRating % 1) * 100}%` }}>
                  <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </div>
              </div>
            )}
            
            {/* Empty stars */}
            {Array.from({ length: 5 - Math.ceil(averageRating) }).map((_, i) => (
              <svg key={`empty-${i}`} className="w-5 h-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ))}
          </div>
        </div>
      </div>
      
      {/* Rating insights */}
      <div className="mt-4 grid grid-cols-2 gap-4">
        <div className="bg-green-50 p-3 rounded-lg">
          <div className="text-xs text-gray-500">Positive ratings (4-5)</div>
          <div className="text-lg font-bold text-green-600 mt-1">
            {ratingCounts.filter(r => r.rating >= 4).reduce((sum, r) => sum + r.count, 0)}
            <span className="text-sm font-normal ml-1">
              ({ratingCounts.filter(r => r.rating >= 4).reduce((sum, r) => sum + r.percentage, 0)}%)
            </span>
          </div>
        </div>
        <div className="bg-red-50 p-3 rounded-lg">
          <div className="text-xs text-gray-500">Critical ratings (1-3)</div>
          <div className="text-lg font-bold text-red-600 mt-1">
            {ratingCounts.filter(r => r.rating <= 3).reduce((sum, r) => sum + r.count, 0)}
            <span className="text-sm font-normal ml-1">
              ({ratingCounts.filter(r => r.rating <= 3).reduce((sum, r) => sum + r.percentage, 0)}%)
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RatingDistribution;